import React, { useState, useEffect } from 'react'
import { Edit2, Save, X, Plus, Trash2, RefreshCw } from 'lucide-react'
import { projectsAPI, Project } from '../services/api'
import { PriorityCalculator } from '../services/priorityCalculator'
import TokenDataModal from './TokenDataModal'
import './EditableProjectTable.css'

interface EditableProjectTableProps {
  projects?: Project[]
  onUpdateProject?: (project: Project) => void
  onAddProject?: (project: Project) => void
  onDeleteProject?: (projectId: number) => void
  onProjectSelect?: (project: Project) => void
}

interface EditingCell {
  projectId: number
  field: keyof Project
  value: any
}

const EditableProjectTable: React.FC<EditableProjectTableProps> = ({
  projects: externalProjects,
  onUpdateProject,
  onAddProject,
  onDeleteProject,
  onProjectSelect
}) => {
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editingCell, setEditingCell] = useState<EditingCell | null>(null)
  const [lastSync, setLastSync] = useState<Date | null>(null)
  const [isSyncing, setIsSyncing] = useState(false)
  const [showAddForm, setShowAddForm] = useState(false)
  const [showTokenModal, setShowTokenModal] = useState(false)
  const [selectedProject, setSelectedProject] = useState<Project | null>(null)
  const [newProject, setNewProject] = useState<Project>({
    client_name: '',
    project_common_name: '',
    agreement_status: 'Pre-agreement',
    start_date: '',
    partner_agreements: '',
    project_status: 'Pending',
    timeline_to_resume: '',
    nature_of_work: 'Token Design',
    relationship_manager: '',
    account_manager: '',
    billed: 'Monthly',
    monthly_retainer: undefined,
    months_of_revenue: undefined,
    expected_fiat_revenue: 0,
    token_potential: 0,
    billing_health: 1,
    tge_date: '',
    priority: 1,
    auto: false,
    notes: '',
    core_hours: 'US',
    engagement_style: '',
    legal_notes: '',
  })

  // Use external projects if provided, otherwise load from API
  useEffect(() => {
    if (externalProjects) {
      setProjects(externalProjects)
      setLoading(false)
      return
    }

    const loadProjects = async () => {
      try {
        setIsSyncing(true)
        setLoading(true)
        const response = await projectsAPI.getAll()
        if (response.success) {
          setProjects(response.data || [])
          setLastSync(new Date())
        } else {
          setError('Failed to load projects')
        }
      } catch (err) {
        setError('Error loading projects')
        console.error('Error loading projects:', err)
      } finally {
        setLoading(false)
        setIsSyncing(false)
      }
    }

    // Initial load
    loadProjects()

    // Set up real-time sync - refresh data every 30 seconds
    const syncInterval = setInterval(loadProjects, 30000)

    // Cleanup interval on unmount
    return () => clearInterval(syncInterval)
  }, [externalProjects])

  const startEditingCell = (projectId: number, field: keyof Project, value: any) => {
    setEditingCell({ projectId, field, value })
  }

  const cancelEditingCell = () => {
    setEditingCell(null)
  }

  const saveCell = async () => {
    if (!editingCell) return

    const project = projects.find(p => p.id === editingCell.projectId)
    if (!project) return

    const updatedProject = { ...project, [editingCell.field]: editingCell.value }
    
    try {
      const response = await projectsAPI.update(editingCell.projectId.toString(), updatedProject)
      if (response.success && response.data) {
        setProjects(projects.map(p => p.id === editingCell.projectId ? response.data! : p))
        onUpdateProject?.(response.data)
      }
    } catch (err) {
      console.error('Error updating project:', err)
      setError('Failed to update project')
    } finally {
      setEditingCell(null)
    }
  }

  const handleCellChange = (value: any) => {
    if (!editingCell) return
    setEditingCell({ ...editingCell, value })
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      saveCell()
    } else if (e.key === 'Escape') {
      cancelEditingCell()
    }
  }

  const deleteProject = async (id: number) => {
    if (confirm('Are you sure you want to delete this project?')) {
      try {
        await projectsAPI.delete(id.toString())
        setProjects(projects.filter(p => p.id !== id))
        onDeleteProject?.(id)
      } catch (err) {
        console.error('Error deleting project:', err)
        setError('Failed to delete project')
      }
    }
  }

  const handleAddNewProject = () => {
    setShowAddForm(true)
  }

  const handleSubmitNewProject = async () => {
    try {
      // Calculate expected revenue based on monthly retainer and months
      const calculatedRevenue = (newProject.monthly_retainer || 0) * (newProject.months_of_revenue || 0)
      const projectToSubmit = {
        ...newProject,
        expected_fiat_revenue: calculatedRevenue
      }
      
      const response = await projectsAPI.create(projectToSubmit)
      if (response.success && response.data) {
        setProjects([...projects, response.data])
        setShowAddForm(false)
        setNewProject({
          client_name: '',
          project_common_name: '',
          agreement_status: 'Pre-agreement',
          start_date: '',
          partner_agreements: '',
          project_status: 'Pending',
          timeline_to_resume: '',
          nature_of_work: 'Token Design',
          relationship_manager: '',
          account_manager: '',
          billed: 'Monthly',
          monthly_retainer: undefined,
          months_of_revenue: undefined,
          expected_fiat_revenue: 0,
          token_potential: 0,
          billing_health: 1,
          tge_date: '',
          priority: 1,
          auto: false,
          notes: '',
          core_hours: 'US',
          engagement_style: '',
          legal_notes: '',
        })
      }
    } catch (err) {
      console.error('Error creating project:', err)
      setError('Failed to create project')
    }
  }

  const updateNewProjectField = (field: keyof Project, value: any) => {
    setNewProject({ ...newProject, [field]: value })
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active': return 'bg-green-900 text-green-300'
      case 'Paused': return 'bg-yellow-900 text-yellow-300'
      case 'Pending': return 'bg-blue-900 text-blue-300'
      case 'Completed': return 'bg-gray-700 text-gray-300'
      default: return 'bg-gray-700 text-gray-300'
    }
  }

  const getAgreementStatusColor = (status: string) => {
    switch (status) {
      case 'Agreement Completed': return 'bg-green-900 text-green-300'
      case 'Started // Pre-agreement': return 'bg-yellow-900 text-yellow-300'
      case 'Pre-agreement': return 'bg-blue-900 text-blue-300'
      default: return 'bg-gray-700 text-gray-300'
    }
  }

  const getWorkTypeColor = (type: string) => {
    switch (type) {
      case 'Token Design': return 'bg-purple-900 text-purple-300'
      case 'Multi-discipline': return 'bg-indigo-900 text-indigo-300'
      case 'Market Making/ CEX': return 'bg-pink-900 text-pink-300'
      case 'Fundraising': return 'bg-red-900 text-red-300'
      case 'Treasury Management': return 'bg-orange-900 text-orange-300'
      default: return 'bg-gray-700 text-gray-300'
    }
  }

  const getPriorityColor = (score: number) => {
    const maxScore = PriorityCalculator.getMaxScore()
    const percentage = (score / maxScore) * 100
    
    if (percentage >= 70) return 'bg-red-900 text-red-300'
    if (percentage >= 40) return 'bg-yellow-900 text-yellow-300'
    return 'bg-green-900 text-green-300'
  }

  // Dropdown options for various fields
  const agreementStatusOptions = [
    'Agreement Completed',
    'Started // Pre-agreement', 
    'Pre-agreement'
  ]

  const projectStatusOptions = [
    'Active',
    'Paused',
    'Pending',
    'Completed'
  ]

  const natureOfWorkOptions = [
    'Token Design',
    'Multi-discipline',
    'Market Making/ CEX',
    'Fundraising',
    'Treasury Management'
  ]

  const billedOptions = [
    'Monthly',
    'Once Off'
  ]

  const coreHoursOptions = [
    'US',
    'GLB (Late PST)',
    'HK',
    'UK',
    'EU/GLB'
  ]

  const engagementStyleOptions = [
    'Ideation',
    'Baking Product',
    'Many Cooks',
    'Flakey',
    'Discussion Driven',
    'Bespoke'
  ]

  // Generate date options for the last 2 years
  const generateDateOptions = () => {
    const options = []
    const today = new Date()
    const twoYearsAgo = new Date(today.getFullYear() - 2, today.getMonth(), today.getDate())
    
    for (let d = new Date(twoYearsAgo); d <= today; d.setDate(d.getDate() + 1)) {
      const dateStr = d.toISOString().split('T')[0]
      options.push(dateStr)
    }
    return options.reverse()
  }

  const dateOptions = generateDateOptions()

  // Render cell content based on field type
  const renderCell = (project: Project, field: keyof Project, value: any) => {
    const isEditing = editingCell?.projectId === project.id && editingCell?.field === field

    if (isEditing) {
      switch (field) {
        case 'client_name':
        case 'project_common_name':
          return (
            <input
              type="text"
              value={editingCell.value || ''}
              onChange={(e) => handleCellChange(e.target.value)}
              onKeyDown={handleKeyPress}
              onBlur={saveCell}
              className="input-glass w-full"
              autoFocus
            />
          )
        
        case 'agreement_status':
          return (
            <select
              value={editingCell.value || ''}
              onChange={(e) => handleCellChange(e.target.value)}
              onKeyDown={handleKeyPress}
              onBlur={saveCell}
              className="input-glass w-full"
              autoFocus
            >
              {agreementStatusOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          )
        
        case 'start_date':
          return (
            <select
              value={editingCell.value || ''}
              onChange={(e) => handleCellChange(e.target.value)}
              onKeyDown={handleKeyPress}
              onBlur={saveCell}
              className="input-glass w-full"
              autoFocus
            >
              <option value="">Select date</option>
              {dateOptions.map(date => (
                <option key={date} value={date}>{date}</option>
              ))}
            </select>
          )
        
        case 'project_status':
          return (
            <select
              value={editingCell.value || ''}
              onChange={(e) => handleCellChange(e.target.value)}
              onKeyDown={handleKeyPress}
              onBlur={saveCell}
              className="input-glass w-full"
              autoFocus
            >
              {projectStatusOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          )
        
        case 'nature_of_work':
          return (
            <select
              value={editingCell.value || ''}
              onChange={(e) => handleCellChange(e.target.value)}
              onKeyDown={handleKeyPress}
              onBlur={saveCell}
              className="input-glass w-full"
              autoFocus
            >
              {natureOfWorkOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          )
        
        case 'monthly_retainer':
        case 'months_of_revenue':
          return (
            <input
              type="number"
              value={editingCell.value || ''}
              onChange={(e) => handleCellChange(parseFloat(e.target.value) || undefined)}
              onKeyDown={handleKeyPress}
              onBlur={saveCell}
              className="input-glass w-full"
              placeholder="0"
              autoFocus
            />
          )
        
        default:
          return (
            <input
              type="text"
              value={editingCell.value || ''}
              onChange={(e) => handleCellChange(e.target.value)}
              onKeyDown={handleKeyPress}
              onBlur={saveCell}
              className="input-glass w-full"
              autoFocus
            />
          )
      }
    } else {
      // Display mode
      switch (field) {
        case 'client_name':
          return (
            <div 
              className="text-sm font-medium text-white cursor-pointer hover:bg-blue-500/10 p-1 rounded"
              onClick={() => startEditingCell(project.id!, field, value)}
            >
              {value}
            </div>
          )
        
        case 'project_common_name':
          return (
            <div 
              className="text-sm text-gray-300 cursor-pointer hover:bg-blue-500/10 p-1 rounded"
              onClick={() => startEditingCell(project.id!, field, value)}
            >
              {value}
            </div>
          )
        
        case 'agreement_status':
          return (
            <span 
              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getAgreementStatusColor(value || '')} cursor-pointer hover:opacity-80`}
              onClick={() => startEditingCell(project.id!, field, value)}
            >
              {value}
            </span>
          )
        
        case 'start_date':
          return (
            <div 
              className="text-sm text-gray-300 cursor-pointer hover:bg-blue-500/10 p-1 rounded"
              onClick={() => startEditingCell(project.id!, field, value)}
            >
              {value}
            </div>
          )
        
        case 'project_status':
          return (
            <span 
              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(value || '')} cursor-pointer hover:opacity-80`}
              onClick={() => startEditingCell(project.id!, field, value)}
            >
              {value}
            </span>
          )
        
        case 'nature_of_work':
          return (
            <span 
              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getWorkTypeColor(value || '')} cursor-pointer hover:opacity-80`}
              onClick={() => startEditingCell(project.id!, field, value)}
            >
              {value}
            </span>
          )
        
        case 'monthly_retainer':
          return (
            <div 
              className="text-sm text-gray-300 cursor-pointer hover:bg-blue-500/10 p-1 rounded"
              onClick={() => startEditingCell(project.id!, field, value)}
            >
              ${value?.toLocaleString() || '0'}
            </div>
          )
        
        case 'months_of_revenue':
          return (
            <div 
              className="text-sm text-gray-300 cursor-pointer hover:bg-blue-500/10 p-1 rounded"
              onClick={() => startEditingCell(project.id!, field, value)}
            >
              {value || '0'}
            </div>
          )
        
        default:
          return (
            <div 
              className="text-sm text-gray-300 cursor-pointer hover:bg-blue-500/10 p-1 rounded"
              onClick={() => startEditingCell(project.id!, field, value)}
            >
              {value}
            </div>
          )
      }
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-2 text-gray-400">Loading projects...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-400">Error: {error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-6 py-8">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <div className="flex space-x-3">
            <button 
              onClick={handleAddNewProject} 
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
            >
              <Plus size={16} />
              <span>Add New Project</span>
            </button>
            <button 
              onClick={() => {
                const loadProjects = async () => {
                  try {
                    setIsSyncing(true)
                    setLoading(true)
                    const response = await projectsAPI.getAll()
                    if (response.success) {
                      setProjects(response.data || [])
                      setLastSync(new Date())
                    } else {
                      setError('Failed to load projects')
                    }
                  } catch (err) {
                    setError('Error loading projects')
                    console.error('Error loading projects:', err)
                  } finally {
                    setLoading(false)
                    setIsSyncing(false)
                  }
                }
                loadProjects()
              }} 
              className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
            >
              <RefreshCw size={16} />
              <span>Refresh</span>
            </button>
          </div>
        </div>
      </div>

      <div className="table-glass">
        <div className="overflow-x-auto relative">
          <table className="min-w-full">
            <thead>
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider sticky left-0 z-10 bg-gray-800/95 backdrop-blur-sm border-r border-gray-700">Client Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Project Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Agreement Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Start Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Project Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Nature of Work</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Monthly Retainer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Months</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Expected Revenue</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Priority Score</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Token Data</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {projects.map((project, index) => (
                <tr 
                  key={project.id || `new-${index}`} 
                  className={`hover:bg-blue-500/5 ${onProjectSelect ? 'cursor-pointer' : ''} transition-colors`}
                  onClick={onProjectSelect ? () => onProjectSelect(project) : undefined}
                >
                  {/* Client Name - Frozen Column */}
                  <td className="px-6 py-4 whitespace-nowrap sticky left-0 z-10 bg-gray-800/95 backdrop-blur-sm border-r border-gray-700">
                    {renderCell(project, 'client_name', project.client_name)}
                  </td>

                  {/* Project Common Name */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    {renderCell(project, 'project_common_name', project.project_common_name)}
                  </td>

                  {/* Agreement Status */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    {renderCell(project, 'agreement_status', project.agreement_status)}
                  </td>

                  {/* Start Date */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    {renderCell(project, 'start_date', project.start_date)}
                  </td>

                  {/* Project Status */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    {renderCell(project, 'project_status', project.project_status)}
                  </td>

                  {/* Nature of Work */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    {renderCell(project, 'nature_of_work', project.nature_of_work)}
                  </td>

                  {/* Monthly Retainer */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    {renderCell(project, 'monthly_retainer', project.monthly_retainer)}
                  </td>

                  {/* Months of Revenue */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    {renderCell(project, 'months_of_revenue', project.months_of_revenue)}
                  </td>

                  {/* Expected Fiat Revenue (Computed) */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-300">
                      ${((project.monthly_retainer || 0) * (project.months_of_revenue || 0)).toLocaleString()}
                    </div>
                  </td>

                  {/* Priority Score */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm">
                      {project.priority_score ? (
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(project.priority_score)}`}>
                          {Math.round(project.priority_score)} pts
                        </span>
                      ) : (
                        <span className="text-gray-500">-</span>
                      )}
                    </div>
                  </td>

                  {/* Token Data Indicator */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm">
                      {(project.token_has_vesting_schedule || project.token_has_financial_data) ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            setSelectedProject(project)
                            setShowTokenModal(true)
                          }}
                          className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-500/20 text-green-400 border border-green-500/30 hover:bg-green-500/30 transition-colors cursor-pointer"
                        >
                          💎 Token Data
                        </button>
                      ) : (
                        <span className="text-gray-500">-</span>
                      )}
                    </div>
                  </td>

                  {/* Actions */}
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      {project.id && (
                        <button 
                          onClick={() => project.id && deleteProject(project.id)} 
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add New Project Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-900 rounded-lg p-6 w-full max-w-2xl mx-4 border border-gray-700">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-white">Add New Project</h3>
              <button 
                onClick={() => setShowAddForm(false)}
                className="text-gray-400 hover:text-white"
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Client Name</label>
                <input
                  type="text"
                  value={newProject.client_name}
                  onChange={(e) => updateNewProjectField('client_name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-800 text-white"
                  placeholder="Enter client name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Project Name</label>
                <input
                  type="text"
                  value={newProject.project_common_name}
                  onChange={(e) => updateNewProjectField('project_common_name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-800 text-white"
                  placeholder="Enter project name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Agreement Status</label>
                <select
                  value={newProject.agreement_status}
                  onChange={(e) => updateNewProjectField('agreement_status', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-800 text-white"
                >
                  {agreementStatusOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Start Date</label>
                <select
                  value={newProject.start_date}
                  onChange={(e) => updateNewProjectField('start_date', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-800 text-white"
                >
                  <option value="">Select date</option>
                  {dateOptions.map(date => (
                    <option key={date} value={date}>{date}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Project Status</label>
                <select
                  value={newProject.project_status}
                  onChange={(e) => updateNewProjectField('project_status', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-800 text-white"
                >
                  {projectStatusOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Nature of Work</label>
                <select
                  value={newProject.nature_of_work}
                  onChange={(e) => updateNewProjectField('nature_of_work', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-800 text-white"
                >
                  {natureOfWorkOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Monthly Retainer</label>
                <input
                  type="number"
                  value={newProject.monthly_retainer || ''}
                  onChange={(e) => updateNewProjectField('monthly_retainer', parseFloat(e.target.value) || undefined)}
                  className="w-full px-3 py-2 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-800 text-white"
                  placeholder="0"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Months of Revenue</label>
                <input
                  type="number"
                  value={newProject.months_of_revenue || ''}
                  onChange={(e) => updateNewProjectField('months_of_revenue', parseFloat(e.target.value) || undefined)}
                  className="w-full px-3 py-2 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-800 text-white"
                  placeholder="0"
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowAddForm(false)}
                className="px-4 py-2 text-gray-300 hover:text-white border border-gray-600 rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitNewProject}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
              >
                Add Project
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Token Data Modal */}
      {showTokenModal && selectedProject && (
        <TokenDataModal
          projectId={selectedProject.id!}
          projectName={selectedProject.client_name}
          isOpen={showTokenModal}
          onClose={() => {
            setShowTokenModal(false)
            setSelectedProject(null)
          }}
        />
      )}
    </div>
  )
}

export default EditableProjectTable 